import React, { useMemo, useRef, useState, Suspense, useCallback, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Sparkles, Stars, Float, Environment, Lightformer } from "@react-three/drei";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

/* device-aware quality so we never render high-poly refraction on weak GPUs */
export function detectQuality() {
  if (typeof navigator === "undefined") return "high";
  const mem = navigator.deviceMemory || 8;
  const cores = navigator.hardwareConcurrency || 8;
  const mobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "");
  if (mem <= 4 || cores <= 4 || mobile) return "low";
  return "high";
}

/* ---------------------------- collectible shard --------------------------- */
function Shard({ data, color, quality, onCollect }) {
  const ref = useRef();
  const [collected, setCollected] = useState(false);
  const [hover, setHover] = useState(false);
  const s = useRef(1);

  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;
    if (!ref.current) return;
    if (collected) {
      s.current = THREE.MathUtils.damp(s.current, 0, 10, dt);
      ref.current.scale.setScalar(s.current);
      if (s.current < 0.02) ref.current.visible = false;
      return;
    }
    const a = data.angle + t * data.speed;
    ref.current.position.set(Math.cos(a) * data.radius, data.y + Math.sin(t * data.bob + data.phase) * 0.6, Math.sin(a) * data.radius);
    ref.current.rotation.x = t * 0.7;
    ref.current.rotation.y = t * 1.0;
    s.current = THREE.MathUtils.damp(s.current, hover ? 1.45 : 1, 8, dt);
    ref.current.scale.setScalar(s.current * data.size);
  });

  const grab = useCallback((e) => { e.stopPropagation(); if (collected) return; setCollected(true); onCollect(data.id); }, [collected, data.id, onCollect]);

  return (
    <mesh ref={ref} onClick={grab}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
      onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}>
      <icosahedronGeometry args={[1, quality === "high" ? 1 : 0]} />
      {quality === "high" ? (
        <meshPhysicalMaterial color={color} emissive={color} emissiveIntensity={hover ? 1.4 : 0.45}
          transmission={0.9} ior={1.6} thickness={0.7} roughness={0.04} metalness={0}
          clearcoat={1} clearcoatRoughness={0.06} envMapIntensity={2.4}
          attenuationColor={color} attenuationDistance={1.6} specularIntensity={1} />
      ) : (
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={hover ? 1.8 : 0.9} metalness={0.5} roughness={0.16} />
      )}
    </mesh>
  );
}

/* ----------------------------- core structures ---------------------------- */
function Structure({ type, accent, quality, onPulse }) {
  const group = useRef();
  const pulse = useRef(0);
  const [hover, setHover] = useState(false);

  useFrame((state, dt) => {
    pulse.current = THREE.MathUtils.damp(pulse.current, 0, 4, dt);
    if (group.current) {
      group.current.rotation.y += dt * 0.12;
      group.current.scale.setScalar(1 + pulse.current * 0.12);
    }
  });

  const hit = (e) => { e.stopPropagation(); pulse.current = 1; onPulse?.(); };
  const ei = hover ? 1.0 : 0.22;

  const mat = (c = accent, extra = {}) => quality === "high" ? (
    <meshPhysicalMaterial color={c} emissive={c} emissiveIntensity={ei} metalness={0.2} roughness={0.06}
      transmission={0.65} ior={1.6} thickness={1.2} clearcoat={1} clearcoatRoughness={0.05}
      envMapIntensity={2.4} attenuationColor={c} attenuationDistance={3} {...extra} />
  ) : (
    <meshStandardMaterial color={c} emissive={c} emissiveIntensity={ei} metalness={0.5} roughness={0.3} {...extra} />
  );
  const solid = (c = accent, extra = {}) => (
    <meshPhysicalMaterial color={c} emissive={c} emissiveIntensity={ei} metalness={0.92} roughness={0.08} clearcoat={1} envMapIntensity={2.6} {...extra} />
  );

  const variants = {
    crystal: (
      <group>
        {[[0, 0, 0, 1.6], [1.2, 0.6, 0.4, 1], [-1, 0.4, -0.6, 0.9], [0.3, 1.6, 0.2, 0.8], [-0.6, -0.8, 0.7, 0.7]].map((p, i) => (
          <mesh key={i} position={[p[0], p[1], p[2]]} rotation={[i, i * 0.6, 0]}>
            <icosahedronGeometry args={[p[3], quality === "high" ? 1 : 0]} />{mat()}
          </mesh>
        ))}
      </group>
    ),
    canopy: (
      <group>
        <mesh position={[0, -1.4, 0]}><cylinderGeometry args={[0.25, 0.6, 3, 24]} />{solid("#1b3a2a")}</mesh>
        {[[0, 1.2, 0, 1.4], [1, 0.8, 0.5, 0.9], [-1, 0.9, -0.4, 1], [0.4, 1.8, -0.6, 0.8], [-0.5, 1.6, 0.7, 0.7]].map((p, i) => (
          <mesh key={i} position={[p[0], p[1], p[2]]}><icosahedronGeometry args={[p[3], quality === "high" ? 2 : 1]} />{mat()}</mesh>
        ))}
      </group>
    ),
    ring: (
      <group>
        <mesh><sphereGeometry args={[1.2, 64, 64]} />{solid()}</mesh>
        <mesh rotation={[Math.PI / 2.4, 0, 0]}><torusGeometry args={[2.6, 0.18, 24, 128]} />{solid(accent, { emissiveIntensity: ei * 1.4 })}</mesh>
        <mesh rotation={[Math.PI / 2.4, 0, 0]}><torusGeometry args={[3.1, 0.06, 16, 128]} />{solid()}</mesh>
      </group>
    ),
    tunnel: <TunnelRings accent={accent} ei={ei} />,
    tower: (
      <group>
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={i} position={[0, i * 0.9 - 1.6, 0]} rotation={[0, i * 0.4, 0]}>
            <cylinderGeometry args={[1.1 - i * 0.12, 1.2 - i * 0.12, 0.7, 6]} />{solid(accent, { wireframe: i % 2 === 1 })}
          </mesh>
        ))}
      </group>
    ),
    dunes: (
      <group>
        <mesh position={[-2.4, 1.6, -3]}><sphereGeometry args={[0.7, 48, 48]} />{solid("#FFD9A0", { emissiveIntensity: 3 })}</mesh>
        <mesh position={[2.2, 1.1, -3]}><sphereGeometry args={[0.45, 48, 48]} />{solid(accent, { emissiveIntensity: 2.4 })}</mesh>
        {[[0, -1.2, 0, 2.4], [-2.2, -1.6, 0.5, 1.6], [2.4, -1.5, -0.4, 1.8]].map((p, i) => (
          <mesh key={i} position={[p[0], p[1], p[2]]} scale={[p[3], p[3] * 0.4, p[3]]}><sphereGeometry args={[1, 48, 32]} />{solid("#5a2f12", { emissiveIntensity: 0.3, metalness: 0.3, roughness: 0.6 })}</mesh>
        ))}
      </group>
    ),
    cathedral: (
      <group>
        <mesh><icosahedronGeometry args={[1, quality === "high" ? 2 : 1]} />{mat(accent, { emissiveIntensity: ei * 1.4 })}</mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} rotation={[i, i * 1.2, 0]}><torusKnotGeometry args={[1.7 + i * 0.5, 0.06, 160, 24, 2, 3]} />{solid()}</mesh>
        ))}
      </group>
    ),
  };

  return (
    <group ref={group} onClick={hit}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }}
      onPointerOut={() => setHover(false)}>
      {variants[type] || variants.crystal}
    </group>
  );
}

function TunnelRings({ accent, ei }) {
  const g = useRef();
  const rings = useMemo(() => Array.from({ length: 14 }, (_, i) => i), []);
  useFrame((state, dt) => {
    if (!g.current) return;
    g.current.children.forEach((c) => {
      c.position.z += dt * 6;
      if (c.position.z > 4) c.position.z -= 28;
      c.rotation.z += dt * 0.4;
    });
  });
  return (
    <group ref={g}>
      {rings.map((i) => (
        <mesh key={i} position={[0, 0, -i * 2]}>
          <torusGeometry args={[2.2, 0.08, 16, 64]} />
          <meshPhysicalMaterial color={accent} emissive={accent} emissiveIntensity={ei} metalness={0.85} roughness={0.12} clearcoat={1} envMapIntensity={2} />
        </mesh>
      ))}
    </group>
  );
}

/* --------------------------- bloom + AA post pass ------------------------- */
function Effects({ quality }) {
  const { gl, scene, camera, size } = useThree();
  const composer = useRef();
  useEffect(() => {
    const dpr = gl.getPixelRatio();
    const rt = new THREE.WebGLRenderTarget(Math.floor(size.width * dpr), Math.floor(size.height * dpr),
      { type: THREE.HalfFloatType, samples: quality === "high" ? 4 : 0 });
    const c = new EffectComposer(gl, rt);
    c.setPixelRatio(dpr);
    c.setSize(size.width, size.height);
    c.addPass(new RenderPass(scene, camera));
    c.addPass(new UnrealBloomPass(new THREE.Vector2(size.width, size.height), quality === "high" ? 0.55 : 0.4, 0.45, 0.78));
    c.addPass(new OutputPass());
    composer.current = c;
    return () => { c.dispose?.(); rt.dispose(); };
  }, [gl, scene, camera, quality]);
  useEffect(() => { composer.current?.setSize(size.width, size.height); }, [size]);
  useFrame(() => { composer.current && composer.current.render(); }, 1);
  return null;
}

/* ------------------------------- the scene -------------------------------- */
function Scene({ world, accent, quality, onCollect, onPulse }) {
  const count = Math.min(quality === "high" ? 12 : 6, world.shards);
  const shards = useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i, angle: Math.random() * Math.PI * 2, radius: 3.4 + Math.random() * 2.8,
    y: -1.5 + Math.random() * 3.5, speed: 0.15 + Math.random() * 0.25,
    bob: 0.6 + Math.random() * 0.8, phase: Math.random() * Math.PI * 2, size: 0.2 + Math.random() * 0.12,
  })), [count]);

  return (
    <>
      <color attach="background" args={[world.sky || "#03040a"]} />
      <fog attach="fog" args={[world.fog || "#04060d", 9, 28]} />
      <ambientLight intensity={0.6} />
      <directionalLight position={[3, 8, 4]} intensity={2.2} color="#ffffff" />
      <pointLight position={[5, 6, 5]} intensity={95} color="#ffffff" />
      <pointLight position={[-6, -2, -4]} intensity={40} color={accent} />
      <Stars radius={60} depth={40} count={quality === "high" ? 1800 : 700} factor={3} fade speed={0.5} />
      <Sparkles count={quality === "high" ? 110 : 50} scale={[16, 12, 16]} size={3} speed={0.3} color={accent} opacity={0.5} />

      <Suspense fallback={null}>
        <Float speed={1.2} rotationIntensity={0.3} floatIntensity={0.6}>
          <Structure type={world.structure} accent={accent} quality={quality} onPulse={onPulse} />
        </Float>
        {shards.map((d) => (
          <Shard key={d.id} data={d} color={world.shard_color || accent} quality={quality} onCollect={onCollect} />
        ))}
        <Environment resolution={quality === "high" ? 256 : 128}>
          <Lightformer intensity={3} position={[0, 4, 3]} scale={[10, 4, 1]} color={accent} />
          <Lightformer intensity={2.4} position={[-5, 1, 2]} scale={[3, 6, 1]} color="#ffffff" />
          <Lightformer intensity={2} position={[5, -1, 2]} scale={[4, 5, 1]} color={world.shard_color || accent} />
          <Lightformer intensity={1.4} position={[0, -3, -4]} scale={[10, 4, 1]} color="#ffffff" />
        </Environment>
      </Suspense>

      <OrbitControls enablePan enableZoom minDistance={3} maxDistance={22}
        autoRotate autoRotateSpeed={0.25} enableDamping dampingFactor={0.08}
        panSpeed={0.6} rotateSpeed={0.65} zoomSpeed={0.9}
        minPolarAngle={Math.PI / 8} maxPolarAngle={Math.PI / 1.25}
        touches={{ ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN }} />
      <Effects quality={quality} />
    </>
  );
}

export default function WorldEngine({ world, accent, quality, onCollect, onPulse }) {
  const q = quality || detectQuality();
  return (
    <Canvas shadows={false} camera={{ position: [0, 1, 11], fov: 45 }}
      dpr={[1, q === "high" ? 2 : 1.4]}
      gl={{ antialias: false, alpha: false, preserveDrawingBuffer: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.15 }}>
      <Scene world={world} accent={accent} quality={q} onCollect={onCollect} onPulse={onPulse} />
    </Canvas>
  );
}
